import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import {
    Box,
    Typography,
    Rating,
    FormControl,
    FormLabel,
    FormControlLabel,
    Checkbox,
    Divider,
    IconButton,
    Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AcxButton from "../../../../UI/AcxButton";
import AcxTextField from "../../../../UI/AcxTextField";

// This is the review feedback structure for the UI (not the same as API feedback)
interface ReviewFeedback {
    id?: string;
    chatId: string;
    overallRating: number;
    responseAccuracy: "excellent" | "good" | "fair" | "poor";
    responseTime: "excellent" | "good" | "fair" | "poor";
    issues: string[];
    suggestions: string;
    wouldRecommend: boolean | null;
    createdAt?: Date;
    createdBy?: string;
}

type Props = {
    chatId: string;
    onClose: () => void;
};

const issueOptions = [
    "Incorrect information provided",
    "Response was too slow",
    "Agent didn't understand the question",
    "Issue not fully resolved",
    "Poor communication",
    "Technical problems",
    "Other",
];

const ChatFeedbackPanel: React.FC<Props> = observer(({ chatId, onClose }) => {
    const [feedback, setFeedback] = useState<ReviewFeedback>({
        chatId,
        overallRating: 0,
        responseAccuracy: "good",
        responseTime: "good",
        issues: [],
        suggestions: "",
        wouldRecommend: null,
    });
    const [existingFeedback,] = useState<ReviewFeedback | null>(null);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    const handleIssueToggle = (issue: string) => {
        setFeedback(prev => ({
            ...prev,
            issues: prev.issues.includes(issue)
                ? prev.issues.filter(i => i !== issue)
                : [...prev.issues, issue],
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            setSaved(true);
        } catch (error) {
            console.error("Failed to save feedback:", error);
        } finally {
            setSaving(false);
        }
    };

    const isValid = feedback.overallRating > 0 && feedback.wouldRecommend !== null;

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h6">
                    Feedback & Review
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </Box>
            
            <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
                {existingFeedback && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Previous feedback submitted by {existingFeedback.createdBy} on{" "}
                        {existingFeedback.createdAt?.toLocaleDateString()}
                    </Alert>
                )}
                
                <FormControl component="fieldset" sx={{ mb: 3, width: "100%" }}>
                    <FormLabel component="legend">Overall Rating</FormLabel>
                    <Rating
                        value={feedback.overallRating}
                        onChange={(e, value) => setFeedback(prev => ({ ...prev, overallRating: value || 0 }))}
                        size="large"
                        sx={{ mt: 1 }}
                    />
                </FormControl>
                
                <Divider sx={{ my: 2 }} />
                
                <FormControl component="fieldset" sx={{ mb: 3, width: "100%" }}>
                    <FormLabel component="legend">Issues Encountered</FormLabel>
                    <Box sx={{ mt: 1 }}>
                        {issueOptions.map(issue => (
                            <FormControlLabel
                                key={issue}
                                control={
                                    <Checkbox
                                        checked={feedback.issues.includes(issue)}
                                        onChange={() => handleIssueToggle(issue)}
                                    />
                                }
                                label={issue}
                            />
                        ))}
                    </Box>
                </FormControl>
                
                <AcxTextField
                    id="feedback-suggestions"
                    type="text"
                    fullWidth
                    label="Additional Comments & Suggestions"
                    placeholder=""
                    helperText=""
                    error={false}
                    required={false}
                    onChange={(e) => setFeedback(prev => ({ ...prev, suggestions: e.target.value }))}
                    other={{
                        value: feedback.suggestions,
                        multiline: true,
                        rows: 4,
                        sx: { mb: 3 }
                    }}
                />
            </Box>
            
            <Box sx={{ p: 2, borderTop: 1, borderColor: "divider", display: "flex", gap: 1 }}>
                <AcxButton
                    variant="contained"
                    onClick={handleSave}
                    disabled={!isValid || saving}
                    fullWidth
                    loading={saving}
                >
                    {existingFeedback ? "Update Feedback" : "Submit Feedback"}
                </AcxButton>
                <AcxButton
                    variant="outlined"
                    onClick={onClose}
                    fullWidth
                    color="white"
                >
                    Cancel
                </AcxButton>
            </Box>
            
            {saved && (
                <Alert severity="success" sx={{ position: "absolute", bottom: 80, left: 16, right: 16 }}>
                    Feedback saved successfully!
                </Alert>
            )}
        </Box>
    );
});

export default ChatFeedbackPanel;